import type { CategoryWithItems } from '@restaurant/core'

/**
 * Shared /stores/menu-items -> CategoryWithItems[] transform.
 *
 * This used to be duplicated verbatim in two places: the useMenuData hook
 * and main.tsx's prefetchMenuData. Since the menuStoreId dedupe (a53b0d72),
 * the prefetch copy is the one that actually wins on cold loads — the hook
 * bails out early via the dedupe check once the prefetch has already
 * populated the store for that storeId. That means any drift between the
 * two copies silently ships in the copy nobody is testing against: a
 * restaurantId drift between them already made it to production once. This
 * module is the single source of truth so that can't happen again.
 *
 * apps/mobile has its own near-identical transform (useMenuData.ts) that is
 * deliberately NOT folded into this module: it emits `isDefault: false` on
 * customization options — a different output shape than web's — and mobile
 * ships on its own per-tenant OTA cadence, independent of web's release
 * pipeline. Unifying the two would couple web deploys to mobile's OTA
 * rollout risk for no shared benefit.
 */

/** Raw customization-item row read from the Flask /stores/menu-items payload. */
export interface RawMenuCustomizationItem {
  customization_item_id: string
  name: string
  additional_price?: number
}

/** Raw customization-option row read from the Flask /stores/menu-items payload. */
export interface RawMenuCustomizationOption {
  customization_option_id: string
  title: string
  is_required?: boolean
  is_multi_select?: boolean
  max_selections?: number
  items?: RawMenuCustomizationItem[]
}

/** Raw category descriptor attached to each Flask menu item. */
export interface RawMenuCategory {
  category_id: string
  name: string
  description?: string
  display_order?: number
  is_active?: boolean
}

/** Raw menu-item row read from the Flask /stores/menu-items payload. */
export interface RawMenuDataItem {
  id: string
  clover_id?: string
  price_variants?: Record<string, number>
  name: string
  description?: string
  price?: number
  image_url?: string
  category: RawMenuCategory
  customization_options?: RawMenuCustomizationOption[]
  calories?: number
  preparation_time?: number
  is_popular?: boolean
  is_featured?: boolean
  is_vegetarian?: boolean
  is_vegan?: boolean
  is_gluten_free?: boolean
  spicy_level?: number
  allergens?: string[]
  tags?: string[]
  display_order?: number
  created_at?: string
  updated_at?: string
}

export function transformMenuResponse(
  response: Record<string, RawMenuDataItem[]>,
  storeId: string
): CategoryWithItems[] {
  const menuItems = response[storeId] || []

  // Group items by category
  const categoryMap = new Map<string, { category: RawMenuCategory; items: RawMenuDataItem[] }>()

  menuItems.forEach((item) => {
    const categoryId = item.category.category_id

    if (!categoryMap.has(categoryId)) {
      categoryMap.set(categoryId, {
        category: item.category,
        items: []
      })
    }

    categoryMap.get(categoryId)!.items.push(item)
  })

  // Transform to Category format expected by the store.
  // The produced customization options intentionally omit `isDefault` (the
  // Flask payload does not provide it); cast to the store shape to preserve
  // the existing runtime structure without fabricating that field.
  const categories = Array.from(categoryMap.values())
    .map(({ category, items }) => ({
      id: category.category_id,
      name: category.name,
      description: category.description || '',
      displayOrder: category.display_order || 0,
      isActive: category.is_active ?? true,
      restaurantId: storeId,
      items: items.map((item) => ({
        id: item.id,
        cloverId: item.clover_id || '',
        priceVariants: item.price_variants || {},
        name: item.name,
        description: item.description || '',
        price: item.price || 0,
        imageUrl: item.image_url,
        categoryId: category.category_id,
        customizations: item.customization_options?.map((opt) => ({
          id: opt.customization_option_id,
          name: opt.title,
          isRequired: opt.is_required || false,
          isMultiSelect: opt.is_multi_select || false,
          minSelections: opt.is_required ? 1 : 0,
          maxSelections: opt.max_selections || (opt.is_multi_select ? 99 : 1),
          options: opt.items?.map((optItem) => ({
            id: optItem.customization_item_id,
            name: optItem.name,
            price: optItem.additional_price || 0,
            maxQuantity: 10
          })) || []
        })) || [],
        calories: item.calories,
        preparationTime: item.preparation_time,
        isPopular: item.is_popular || false,
        isFeatured: item.is_featured || false,
        isVegetarian: item.is_vegetarian || false,
        isVegan: item.is_vegan || false,
        isGlutenFree: item.is_gluten_free || false,
        spicyLevel: item.spicy_level || 0,
        allergens: item.allergens || [],
        tags: item.tags || [],
        displayOrder: item.display_order || 0,
        createdAt: item.created_at || new Date().toISOString(),
        updatedAt: item.updated_at || new Date().toISOString()
      }))
    }))
    .sort((a, b) => a.displayOrder - b.displayOrder) as unknown as CategoryWithItems[]

  return categories
}
